import React, { useEffect, useMemo, useRef, useState } from "react";
import { Typography, Button, Box } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import * as styles from "./Styles.js";
import SearchBoxComponent from "../../components/SearchBox/SearchBoxComponent.js";
import ApplyButton from "../../components/Buttons/ApplyButton.js";
import IBsFullReportRequest from "../../Endpoints/IBsFullReportRequest.js";
import TitledMultiSelector from "../../components/Selectors/TitledMultiSelector.js";
import TableComponent from "../../components/Table/TableComponent.js";
import {
  getLocalTimeZoneOffsetInSeconds,
  getTimestamps,
} from "../../Tools/DateTimeConvertions.js";
import DateSelector from "../../components/Selectors/DateSelector.js";
import TitledSelector from "../../components/Selectors/TitledSelector.js";
import IconComponent from "../../components/Icons/IconComponent.js";
import TableToolTip from "../../components/ToolTips/TableToolTip.js";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import LoadingSpinner from "../../components/Loaders/SpinLoader.js";
import {
  DATE_RANGE_OPTIONS,
  ROWS_PER_PAGE_OPTIONS,
  DEFAULT_ROWS_PER_PAGE,
  TRANSACTION_TYPE_OPTIONS,
  buildFullReportRow,
  computeNextIbSelection,
  fmt,
  parseFullReportDateToMs,
  resolveSelectedValues,
} from "../../Tools/IbsReportUtils.js";

// Columns match the /ib-report-full response 1:1 (flat, transaction-level
// rows): date, ib_id, ib_name, ib_email, transaction_type, amount, volume,
// currency, platform, account, ticket, comments. Reuses the shared,
// DataGrid-based `TableComponent` - since this data is already flat (one
// row per transaction, no parent/child grouping), it's a direct fit.
const columns = [
  {
    field: "date",
    headerName: "Date",
    type: "number",
    align: "left",
    headerAlign: "left",
    flex: 1.1,
    minWidth: 160,
    renderCell: (params) => params.row.dateLabel,
  },
  { field: "ib_id", headerName: "IB ID", type: "number", flex: 0.6, minWidth: 90 },
  { field: "name", headerName: "Name", flex: 1, minWidth: 140 },
  { field: "ib_email", headerName: "Email", flex: 1.3, minWidth: 200 },
  { field: "transactionType", headerName: "Type", flex: 0.7, minWidth: 110 },
  {
    field: "amount",
    headerName: "Amount",
    type: "number",
    flex: 0.8,
    minWidth: 110,
    renderCell: (params) => fmt(params.value),
  },
  {
    field: "volume",
    headerName: "Volume",
    type: "number",
    flex: 0.7,
    minWidth: 100,
    renderCell: (params) => fmt(params.value),
  },
  { field: "currency", headerName: "CCY", flex: 0.5, minWidth: 80 },
  { field: "platform", headerName: "Platform", flex: 1, minWidth: 140 },
  { field: "account", headerName: "Account", flex: 0.8, minWidth: 120 },
  { field: "ticket", headerName: "Ticket", flex: 0.7, minWidth: 100 },
  { field: "comments", headerName: "Comments", flex: 1.4, minWidth: 220 },
];

const DEFAULT_SORT_MODEL = [{ field: "date", sort: "desc" }];

// Full Report tab: reuses the same selectors as the Summarized Report tab
// (period selector, IB selector), plus a "Transaction type" filter and a
// "Trees" filter (only shown when the user's own trees are known, i.e. the
// Master IB trees from Tools/MasterIbChecker.js) - but hits
// POST /ib-report-full.
function FullReportTab({
  name,
  type,
  canUseIbSelector,
  ibOptions,
  individualEmails,
  ibOptionsLoading,
  masterTreeIds,
  masterTrees,
}) {
  const [dateSelect, setDateSelect] = useState("Today");
  const [showDataPickers, setShowDataPickers] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [brokerSelect, setBrokerSelect] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState(
    TRANSACTION_TYPE_OPTIONS.map((option) => option.value)
  );

  // "Trees" multi-selector: id + name of every tree this user is Master IB
  // of. Only meaningful/shown when at least one such tree is known - BROKER
  // accounts don't have a "my trees" list wired up on this page yet.
  const treeOptions = useMemo(
    () =>
      (masterTrees || [])
        .filter((tree) => tree?.id !== null && tree?.id !== undefined)
        .map((tree) => ({
          label: `${tree?.description || tree?.group || "Tree"} (#${tree.id})`,
          value: String(tree.id),
        })),
    [masterTrees]
  );
  const treeOptionValues = useMemo(() => treeOptions.map((option) => option.value), [treeOptions]);
  const showTreeSelector = treeOptions.length > 0;
  const [treeSelect, setTreeSelect] = useState([]);

  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const didInitialFetch = useRef(false);

  const resolveTreeIds = () => {
    if (!showTreeSelector) {
      // No known trees for this account (e.g. BROKER): fall back to
      // whatever ids were resolved elsewhere (today, none).
      return masterTreeIds || [];
    }

    return resolveSelectedValues(treeSelect, treeOptionValues).map((value) => Number(value));
  };

  const RequestIBsFullListData = async (params) => {
    setLoading(true);
    setError("");
    setHasSearched(true);
    try {
      const dataResponse = await IBsFullReportRequest(params);
      if (dataResponse.status == 200) {
        const storedReports =
          dataResponse?.data?.data?.reports ?? dataResponse?.data?.reports ?? [];

        if (Array.isArray(storedReports) && storedReports.length > 0) {
          const actualRows = storedReports.map((report, index) => {
            const row = buildFullReportRow(report, index);
            return { ...row, dateLabel: row.date, date: parseFullReportDateToMs(row.date) };
          });
          setRows(actualRows);
          setFilteredRows(actualRows);
        } else {
          setRows([]);
          setFilteredRows([]);
        }
      }
    } catch (requestError) {
      console.log(requestError);
      setError("We couldn't load the full report.");
      setRows([]);
      setFilteredRows([]);
    }

    setLoading(false);
  };

  // Initial load: same email-sourcing flow as the Summarized Report tab
  // (real emails only, "All" only kicks in from Apply onward), plus the
  // extra `tree_ids`/`transaction_type` params this endpoint expects. Trees
  // default to "All" (every known tree) as soon as they're available.
  useEffect(() => {
    if (!canUseIbSelector || ibOptionsLoading || didInitialFetch.current) return;
    didInitialFetch.current = true;

    setBrokerSelect(["All", ...individualEmails]);
    setTreeSelect(showTreeSelector ? ["All", ...treeOptionValues] : []);
    setRows([]);
    setFilteredRows([]);

    if (!individualEmails.length) return;

    const datetime = getTimestamps("Today");
    RequestIBsFullListData({
      tree_ids: showTreeSelector ? treeOptionValues.map((value) => Number(value)) : masterTreeIds || [],
      ib_emails: individualEmails,
      transaction_type: transactionTypes,
      from: datetime.from,
      to: datetime.to,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUseIbSelector, ibOptionsLoading, individualEmails, showTreeSelector, treeOptionValues]);

  const handleFilteredRows = (rowsevent) => {
    setFilteredRows(rowsevent);
  };

  const handleFromChange = (date) => setFromDate(date);
  const handleToChange = (date) => setToDate(date);

  const handleDownload = (dataArray) => {
    const exportRows = dataArray.map(({ id, date, dateLabel, ...rest }) => ({
      date: dateLabel,
      ...rest,
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportRows);
    XLSX.utils.book_append_sheet(wb, ws, "IbsFullData");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const fecha = new Date();
    const year = fecha.getFullYear();
    const month = String(fecha.getMonth() + 1).padStart(2, "0");
    const day = String(fecha.getDate()).padStart(2, "0");
    saveAs(blob, `${name}_Ibs_Full_${year}_${month}_${day}.xlsx`);
  };

  const handleDateSelector = (event) => {
    setDateSelect(event.target.value);
    setShowDataPickers(event.target.value === "Custom");
  };

  const handleBrokerIbSelector = (selectedValues) => {
    const allOptions = ibOptions.map((item) => item.value);
    setBrokerSelect((prev) => computeNextIbSelection(selectedValues, prev, allOptions));
  };

  const handleTreeSelector = (selectedValues) => {
    setTreeSelect((prev) => computeNextIbSelection(selectedValues, prev, ["All", ...treeOptionValues]));
  };

  const handleTransactionTypeChange = (selectedValues) => {
    setTransactionTypes(selectedValues);
  };

  const HandleOnClick = () => {
    // Unlike the Summarized Report tab, this always resolves to real emails
    // (never sends the synthetic "All" value) since it's a brand new
    // integration with the backend.
    const emails = resolveSelectedValues(brokerSelect, individualEmails);
    const treeIds = resolveTreeIds();

    if (dateSelect != "Custom") {
      const datetime = getTimestamps(dateSelect);
      RequestIBsFullListData({
        tree_ids: treeIds,
        ib_emails: emails,
        transaction_type: transactionTypes,
        from: datetime.from,
        to: datetime.to,
      });
    } else if (fromDate != null || toDate != null) {
      RequestIBsFullListData({
        tree_ids: treeIds,
        ib_emails: emails,
        transaction_type: transactionTypes,
        from: fromDate / 1000 + getLocalTimeZoneOffsetInSeconds(),
        to: toDate / 1000 + getLocalTimeZoneOffsetInSeconds() + 86400,
      });
    }
  };

  const canApply =
    brokerSelect.length > 0 &&
    transactionTypes.length > 0 &&
    (!showTreeSelector || treeSelect.length > 0);

  function NoRowsOverlay() {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          color: "#334155",
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 32, color: "#334155" }} />
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#334155" }}>
          {hasSearched
            ? "No results found. Try adjusting your filters."
            : "No results yet. Set your filters to find matching requests."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {loading ? <LoadingSpinner /> : <></>}

      <Box sx={{ display: "flex", flexDirection: "row", alignItems: "flex-end", flexWrap: "wrap", gap: 2 }}>
        <TitledSelector
          title="Period of time"
          value={dateSelect}
          handleChange={handleDateSelector}
          options={DATE_RANGE_OPTIONS}
        />

        {showDataPickers && (
          <>
            <DateSelector title="From" value={fromDate} handleChange={handleFromChange} />
            <DateSelector title="To" value={toDate} handleChange={handleToChange} />
          </>
        )}

        {canUseIbSelector ? (
          <TitledMultiSelector
            title="IB selector"
            value={brokerSelect}
            handleChange={handleBrokerIbSelector}
            options={ibOptions}
          />
        ) : null}

        {showTreeSelector ? (
          <TitledMultiSelector
            title="Trees"
            value={treeSelect}
            handleChange={handleTreeSelector}
            options={[{ label: "All", value: "All" }, ...treeOptions]}
          />
        ) : null}

        <TitledMultiSelector
          title="Transaction type"
          value={transactionTypes}
          handleChange={handleTransactionTypeChange}
          options={TRANSACTION_TYPE_OPTIONS}
        />

        <TableToolTip title="Filter the full report by period, IB/client, tree and transaction type. All transaction types and trees are included by default.">
          <Box>
            <ApplyButton HandleOnClick={HandleOnClick} disable={!canApply}>
              Apply <IconComponent icon="help" size="12px" />
            </ApplyButton>
          </Box>
        </TableToolTip>

        <Box sx={{ marginLeft: "auto", marginTop: "25px", display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="outlined"
            sx={styles.ExportButton}
            onClick={() => handleDownload(filteredRows)}
            disabled={filteredRows.length == 0}
          >
            <IconComponent icon="download" size="12px" />
            &nbsp;Export
          </Button>

          <SearchBoxComponent
            rows={rows}
            onFilteredRowsChange={handleFilteredRows}
            placeholder="Search by IB ID, name or email"
            filterFields={["ib_id", "name", "ib_email"]}
          />
        </Box>
      </Box>

      {error ? (
        <Typography sx={{ mt: 1.5, color: "error.main", fontWeight: 700 }}>{error}</Typography>
      ) : null}

      <Box sx={{ width: "100%", marginTop: 3 }}>
        <TableComponent
          rows={filteredRows}
          columns={columns}
          sortConfig={DEFAULT_SORT_MODEL}
          pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
          initialPageSize={DEFAULT_ROWS_PER_PAGE}
          slots={{ noRowsOverlay: NoRowsOverlay }}
        />
      </Box>
    </Box>
  );
}

export default FullReportTab;